import {
  ChartAdapter,
  ChartBuildContext,
  EChartsOption,
  HeatmapChartSettings,
} from "../types";

import {
  buildLegend,
  buildTooltip,
  buildVisualMap,
  mergeHeatmapSettings,
} from "./shared";

function getHeatmapStats(values: (number | null)[][]) {
  let min: number | undefined;
  let max: number | undefined;

  values.forEach((row) => {
    row.forEach((value) => {
      if (value === null || value === undefined) {
        return;
      }
      if (min === undefined || value < min) {
        min = value;
      }
      if (max === undefined || value > max) {
        max = value;
      }
    });
  });

  return { min, max };
}

const heatmapChartAdapter: ChartAdapter<"heatmap"> = {
  type: "heatmap",
  buildOption: (ctx: ChartBuildContext<"heatmap">) => {
    const { data } = ctx;
    const mergedSettings = mergeHeatmapSettings(ctx.settings);

    const { min, max } = getHeatmapStats(data.values);

    const legend = buildLegend(data.legend, mergedSettings.legend);
    const tooltip = buildTooltip(data.tooltip, {
      trigger: mergedSettings.tooltip?.trigger ?? "item",
      enable: mergedSettings.tooltip?.enable,
      formatter: mergedSettings.tooltip?.formatter,
    });

    const visualMap = buildVisualMap(
      data.visualMap,
      mergedSettings.visualMap,
      { min, max }
    );

    const seriesData: any[] = [];
    data.values.forEach((row, rowIndex) => {
      row.forEach((value, columnIndex) => {
        seriesData.push([columnIndex, rowIndex, value ?? null]);
      });
    });

    const series = [
      {
        type: "heatmap",
        data: seriesData,
        itemStyle: mergedSettings.cellBorderColor
          ? { borderColor: mergedSettings.cellBorderColor }
          : undefined,
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowColor: "rgba(0, 0, 0, 0.4)",
          },
        },
      },
    ];

    const option: EChartsOption = {
      xAxis: {
        type: "category",
        data: data.xCategories,
        splitArea: { show: true },
      },
      yAxis: {
        type: "category",
        data: data.yCategories,
        splitArea: { show: true },
      },
      legend,
      tooltip,
      series,
    };

    if (visualMap) {
      option.visualMap = {
        type: "continuous",
        ...visualMap,
      };
    }

    return option;
  },
  getDefaultSettings: (): Partial<HeatmapChartSettings> => ({
    legend: { show: false },
    tooltip: { enable: true, trigger: "item" },
    visualMap: { show: true, calculable: true },
    cellBorderColor: "#ffffff",
  }),
};

export default heatmapChartAdapter;
